import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { ScrollArea } from '../components/ui/scroll-area';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import {
  Globe,
  Search,
  Plus,
  Users,
  LogOut,
  FileDown,
  Building2,
  Phone,
  Mail,
  MapPin,
  Calendar,
  FileText,
  X,
  Edit,
  Trash2,
  Download,
  RefreshCw,
  Info,
  Link2,
  Menu,
  Send
} from 'lucide-react';
import ContactForm from '../components/ContactForm';
import ContactProfile from '../components/ContactProfile';
import AddInteractionModal from '../components/AddInteractionModal';
import EditInteractionForm from '../components/EditInteractionForm';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Dashboard = () => {
  const { user, logout, getAuthHeader } = useAuth();
  const navigate = useNavigate();
  const [contacts, setContacts] = useState([]);
  const [selectedContact, setSelectedContact] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showContactForm, setShowContactForm] = useState(false);
  const [editingContact, setEditingContact] = useState(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [contactToDelete, setContactToDelete] = useState(null);
  const [showInteractionModal, setShowInteractionModal] = useState(false);
  const [editingInteraction, setEditingInteraction] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const fetchContacts = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/contacts`, getAuthHeader());
      setContacts(response.data);
    } catch (error) {
      console.error('Error fetching contacts:', error);
      toast.error('Failed to load contacts');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader]);

  useEffect(() => {
    fetchContacts();
  }, [fetchContacts]);

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(async () => {
      if (searchQuery.length >= 2) {
        setIsSearching(true);
        try {
          const response = await axios.get(
            `${API_URL}/api/search?q=${encodeURIComponent(searchQuery)}`,
            getAuthHeader()
          );
          setSearchResults(response.data);
        } catch (error) {
          console.error('Search error:', error);
        } finally {
          setIsSearching(false);
        }
      } else {
        setSearchResults([]);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [searchQuery, getAuthHeader]);

  const handleLogout = () => {
    logout();
    toast.success('Logged out successfully');
  };

  const handleCreateContact = async (contactData) => {
    try {
      await axios.post(`${API_URL}/api/contacts`, contactData, getAuthHeader());
      toast.success('Contact created successfully');
      fetchContacts();
      setShowContactForm(false);
      setEditingInteraction(null);
    } catch (error) {
      console.error('Error creating contact:', error?.response?.data || error);
      const msg = error?.response?.data?.detail || error?.message || 'Unknown error';
      toast.error(`Failed to create contact: ${msg}`);
    }
  };

  const handleUpdateContact = async (contactData) => {
    try {
      // Check if we're editing or adding an interaction
      const editInteractionData = contactData._editingInteraction;
      const editInteractionId = contactData._editingInteractionId;
      const newInteractionData = contactData._newInteraction;
      
      // Clean the contact data
      const cleanData = { ...contactData };
      delete cleanData._editingInteraction;
      delete cleanData._editingInteractionId;
      delete cleanData._newInteraction;
      
      // Update the contact fields first
      const response = await axios.put(
        `${API_URL}/api/contacts/${editingContact.id}`,
        cleanData,
        getAuthHeader()
      );
      
      let finalContact = response.data;
      
      if (editInteractionData && editInteractionId) {
        // Editing an existing interaction
        const interactionResponse = await axios.put(
          `${API_URL}/api/contacts/${editingContact.id}/interactions/${editInteractionId}`,
          { interaction: editInteractionData },
          getAuthHeader()
        );
        finalContact = interactionResponse.data;
      } else if (newInteractionData) {
        // Adding a new interaction
        const interactionResponse = await axios.post(
          `${API_URL}/api/contacts/${editingContact.id}/interactions`,
          { interaction: newInteractionData },
          getAuthHeader()
        );
        finalContact = interactionResponse.data;
      }
      
      setSelectedContact(finalContact);
      toast.success(newInteractionData ? 'Interaction added successfully' : 'Updated successfully');
      fetchContacts();
      setEditingContact(null);
      setEditingInteraction(null);
      setShowContactForm(false);
    } catch (error) {
      console.error('Error updating contact:', error?.response?.data || error);
      const msg = error?.response?.data?.detail || error?.message || 'Unknown error';
      toast.error(`Failed to save: ${msg}`);
    }
  };

  const handleDeleteContact = async () => {
    if (!contactToDelete) return;
    
    try {
      await axios.delete(
        `${API_URL}/api/contacts/${contactToDelete.id}`,
        getAuthHeader()
      );
      toast.success('Contact deleted successfully');
      fetchContacts();
      if (selectedContact?.id === contactToDelete.id) {
        setSelectedContact(null);
      }
      setShowDeleteDialog(false);
      setContactToDelete(null);
    } catch (error) {
      console.error('Error deleting contact:', error);
      toast.error('Failed to delete contact');
    }
  };

  const handleAddInteraction = async (interactionData) => {
    try {
      const response = await axios.post(
        `${API_URL}/api/contacts/${selectedContact.id}/interactions`,
        { interaction: interactionData },
        getAuthHeader()
      );
      toast.success('Interaction added successfully');
      setSelectedContact(response.data);
      fetchContacts();
      setShowInteractionModal(false);
    } catch (error) {
      console.error('Error adding interaction:', error);
      toast.error('Failed to add interaction');
    }
  };

  const handleEditInteraction = async (interactionData) => {
    try {
      const response = await axios.put(
        `${API_URL}/api/contacts/${selectedContact.id}/interactions/${editingInteraction.id}`,
        { interaction: interactionData },
        getAuthHeader()
      );
      toast.success('Interaction updated');
      setSelectedContact(response.data);
      fetchContacts();
      setShowInteractionModal(false);
      setEditingInteraction(null);
    } catch (error) {
      console.error('Error updating interaction:', error);
      toast.error('Failed to update interaction');
    }
  };


  const handleExportCSV = async () => {
    try {
      const response = await axios.get(
        `${API_URL}/api/export/contacts/csv`,
        {
          ...getAuthHeader(),
          responseType: 'blob'
        }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'contacts.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('Contacts exported successfully');
    } catch (error) {
      console.error('Error exporting contacts:', error);
      toast.error('Failed to export contacts');
    }
  };

  const handleExportWord = async (contact) => {
    try {
      const response = await axios.get(
        `${API_URL}/api/export/contacts/${contact.id}/word`,
        {
          ...getAuthHeader(),
          responseType: 'blob'
        }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${contact.name.replace(/\s+/g, '_')}_profile.docx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('Profile exported to Word');
    } catch (error) {
      console.error('Error exporting to Word:', error);
      toast.error('Failed to export profile');
    }
  };

  const displayContacts = searchQuery.length >= 2 ? searchResults : contacts;

  return (
    <div className="min-h-screen bg-slate-50 lg:flex">
      {/* Mobile Overlay */}
      {sidebarOpen && (
        <div className="fixed inset-0 bg-black/50 z-40 lg:hidden" onClick={() => setSidebarOpen(false)} />
      )}

      {/* Sidebar */}
      <aside className={`w-80 bg-[#0F172A] flex flex-col h-screen fixed left-0 top-0 z-50 transition-transform duration-300 lg:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        {/* Logo */}
        <div className="p-6 border-b border-slate-700">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-[#D4AF37] flex items-center justify-center">
              <Globe className="w-5 h-5 text-white" />
            </div>
            <h1 className="text-xl font-heading font-semibold text-white">Our Orbis</h1>
          </div>
        </div>

        {/* Search */}
        <div className="p-4 border-b border-slate-700">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Search contacts..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              data-testid="search-input"
              className="pl-10 bg-slate-800 border-slate-700 text-white placeholder:text-slate-400 focus:border-[#D4AF37] focus:ring-[#D4AF37]"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
          {isSearching && (
            <p className="text-xs text-slate-400 mt-2">Searching...</p>
          )}
          {searchQuery.length >= 2 && !isSearching && (
            <p className="text-xs text-slate-400 mt-2">
              {searchResults.length} result{searchResults.length !== 1 ? 's' : ''} found
            </p>
          )}
        </div>

        {/* Contact List */}
        <ScrollArea className="flex-1">
          <div className="p-2">
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <div className="w-6 h-6 border-2 border-[#D4AF37]/20 border-t-[#D4AF37] rounded-full animate-spin"></div>
              </div>
            ) : displayContacts.length === 0 ? (
              <div className="text-center py-8 px-4">
                <Users className="w-12 h-12 text-slate-600 mx-auto mb-3" />
                <p className="text-slate-400 text-sm">
                  {searchQuery ? 'No contacts found' : 'No contacts yet'}
                </p>
                {!searchQuery && (
                  <p className="text-slate-500 text-xs mt-1">
                    Click "Add New Contact" to get started
                  </p>
                )}
              </div>
            ) : (
              displayContacts.map((contact, index) => (
                <button
                  key={contact.id}
                  onClick={() => {
                    setSelectedContact(contact);
                    setShowContactForm(false);
                    setEditingContact(null);
                    setSidebarOpen(false);
                  }}
                  data-testid={`contact-item-${index}`}
                  className={`w-full text-left p-3 rounded-lg mb-1 transition-all contact-item ${
                    selectedContact?.id === contact.id 
                      ? 'active bg-[#D4AF37]/15 border-l-3 border-[#D4AF37]' 
                      : 'hover:bg-slate-800'
                  }`}
                  style={{
                    animationDelay: `${index * 50}ms`
                  }}
                >
                  <p className="text-white font-medium truncate">{contact.name}</p>
                  <p className="text-slate-400 text-sm truncate">
                    {contact.current_employer || 'No employer'}
                  </p>
                  {/* Show key contact match indicator */}
                  {contact._key_contact_match && contact._key_contact_match.length > 0 && (
                    <p className="text-[#D4AF37] text-xs mt-1 truncate">
                      {contact._key_contact_match[0].name} appears as Key Contact
                    </p>
                  )}
                </button>
              ))
            )}
          </div>
        </ScrollArea>

        {/* Bottom Actions */}
        <div className="p-4 border-t border-slate-700 space-y-2">
          <Button
            onClick={() => navigate('/future-network')}
            variant="outline"
            data-testid="future-network-button"
            className="w-full justify-start gap-2 border-slate-600 text-slate-300 hover:bg-slate-800 hover:text-white"
          >
            <Users className="w-4 h-4" />
            Future Network
          </Button>
          <Button
            onClick={() => navigate('/catch-up')}
            variant="outline"
            data-testid="catch-up-button"
            className="w-full justify-start gap-2 border-slate-600 text-slate-300 hover:bg-slate-800 hover:text-white"
          >
            <RefreshCw className="w-4 h-4" />
            Catch-Up
          </Button>
          <Button
            onClick={() => navigate('/connections')}
            variant="outline"
            data-testid="connections-button"
            className="w-full justify-start gap-2 border-slate-600 text-slate-300 hover:bg-slate-800 hover:text-white"
          >
            <Link2 className="w-4 h-4" />
            Connections
          </Button>
          <Button
            onClick={() => navigate('/daily-outreaches')}
            variant="outline"
            data-testid="daily-outreaches-button"
            className="w-full justify-start gap-2 border-slate-600 text-slate-300 hover:bg-slate-800 hover:text-white"
          >
            <Send className="w-4 h-4" />
            Daily Outreaches
          </Button>
          <Button
            onClick={handleExportCSV}
            variant="outline"
            data-testid="export-csv-button"
            className="w-full justify-start gap-2 border-[#D4AF37]/50 text-[#D4AF37] hover:bg-[#D4AF37]/10"
          >
            <FileDown className="w-4 h-4" />
            Export to CSV
          </Button>
          <div className="flex items-center justify-between pt-2 border-t border-slate-700">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-slate-700 flex items-center justify-center text-white text-sm font-medium">
                {user?.full_name?.charAt(0) || 'U'}
              </div>
              <span className="text-sm text-slate-300 truncate max-w-[120px]">
                {user?.full_name}
              </span>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleLogout}
              data-testid="logout-button"
              className="text-slate-400 hover:text-white hover:bg-slate-800"
            >
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 lg:ml-80 min-h-screen">
        {/* Top Bar */}
        <header className="bg-white border-b border-slate-200 px-4 sm:px-8 py-4 sticky top-0 z-10">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-3">
              <button
                onClick={() => setSidebarOpen(true)}
                className="lg:hidden p-2 -ml-2 text-slate-600 hover:text-[#0F172A]"
                data-testid="mobile-menu-button"
              >
                <Menu className="w-5 h-5" />
              </button>
              <h2 className="text-lg sm:text-2xl font-heading font-semibold text-[#0F172A] truncate">
                {showContactForm 
                  ? (editingContact ? 'Edit Contact' : 'Add New Contact')
                  : editingInteraction
                    ? 'Edit Interaction'
                    : selectedContact 
                      ? selectedContact.name 
                      : 'Contacts'
                }
              </h2>
            </div>
            <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0">
              {selectedContact && !showContactForm && (
                <>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleExportWord(selectedContact)}
                    data-testid="export-word-button"
                    className="gap-2 border-[#D4AF37]/50 text-[#D4AF37] hover:bg-[#D4AF37]/10 hidden sm:inline-flex"
                  >
                    <Download className="w-4 h-4" />
                    Export to Word
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setEditingContact(selectedContact);
                      setEditingInteraction(null);
                      setShowContactForm(true);
                    }}
                    data-testid="edit-contact-button"
                    className="gap-2"
                  >
                    <Edit className="w-4 h-4" />
                    Edit
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setContactToDelete(selectedContact);
                      setShowDeleteDialog(true);
                    }}
                    data-testid="delete-contact-button"
                    className="gap-2 text-red-600 border-red-200 hover:bg-red-50"
                  >
                    <Trash2 className="w-4 h-4" />
                    Delete
                  </Button>
                </>
              )}
              {!showContactForm && (
                <Button
                  onClick={() => {
                    setShowContactForm(true);
                    setEditingContact(null);
                    setSelectedContact(null);
                    setEditingInteraction(null);
                  }}
                  data-testid="add-contact-button"
                  className="gap-2 bg-[#0F172A] hover:bg-[#1E293B] text-white"
                >
                  <Plus className="w-4 h-4" />
                  <span className="hidden sm:inline">Add New Contact</span>
                </Button>
              )}
              {showContactForm && (
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowContactForm(false);
                    setEditingContact(null);
                    setEditingInteraction(null);
                  }}
                  data-testid="cancel-form-button"
                >
                  Cancel
                </Button>
              )}
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/about')}
                data-testid="about-link"
                className="text-slate-500 hover:text-[#0F172A]"
              >
                <Info className="w-4 h-4 mr-1" />
                About
              </Button>
            </div>
          </div>
        </header>

        {/* Content Area */}
        <div className="p-4 sm:p-8">
          {showContactForm ? (
            <ContactForm
              contact={editingContact}
              editingInteraction={editingInteraction}
              onSubmit={editingContact ? handleUpdateContact : handleCreateContact}
              onCancel={() => {
                setShowContactForm(false);
                setEditingContact(null);
                setEditingInteraction(null);
              }}
            />
          ) : editingInteraction && selectedContact ? (
            <EditInteractionForm
              interaction={editingInteraction}
              contactId={selectedContact.id}
              contactName={selectedContact.name}
              onSave={async (data) => {
                try {
                  const response = await axios.put(
                    `${API_URL}/api/contacts/${selectedContact.id}/interactions/${editingInteraction.id}`,
                    { interaction: data },
                    getAuthHeader()
                  );
                  toast.success('Interaction updated');
                  setSelectedContact(response.data);
                  fetchContacts();
                  setEditingInteraction(null);
                } catch (error) {
                  console.error('Error updating interaction:', error);
                  toast.error('Failed to update interaction');
                }
              }}
              onCancel={() => setEditingInteraction(null)}
            />
          ) : selectedContact ? (
            <ContactProfile
              contact={selectedContact}
              onAddInteraction={() => {
                setEditingInteraction({ _isNew: true });
                setEditingContact(selectedContact);
                setShowContactForm(true);
              }}
              onEditInteraction={(interaction) => {
                setEditingInteraction(interaction);
                setEditingContact(selectedContact);
                setShowContactForm(true);
              }}
            />
          ) : (
            <div className="flex flex-col items-center justify-center h-[60vh] text-center">
              {contacts.length > 0 ? (
                <>
                  <Users className="w-16 h-16 text-slate-200 mb-6" />
                  <h3 className="text-2xl font-heading font-semibold text-[#0F172A] mb-2">
                    Select a Contact
                  </h3>
                  <p className="text-slate-500 max-w-md">
                    Choose a contact from the sidebar to view their profile, or add a new one.
                  </p>
                </>
              ) : (
                <>
                  <div 
                    className="w-64 h-64 mb-8 rounded-2xl bg-cover bg-center opacity-50"
                    style={{
                      backgroundImage: `url('https://images.unsplash.com/photo-1707047754373-ba65fd3f249f?crop=entropy&cs=srgb&fm=jpg&q=85')`
                    }}
                  ></div>
                  <h3 className="text-2xl font-heading font-semibold text-[#0F172A] mb-2">
                    Welcome to Your Network
                  </h3>
                  <p className="text-slate-500 max-w-md mb-6">
                    Select a contact from the sidebar to view their profile, or add a new contact to get started.
                  </p>
                  <Button
                    onClick={() => {
                      setShowContactForm(true);
                      setEditingInteraction(null);
                    }}
                    data-testid="add-first-contact-button"
                    className="gap-2 bg-[#0F172A] hover:bg-[#1E293B] text-white"
                  >
                    <Plus className="w-4 h-4" />
                    Add Your First Contact
                  </Button>
                </>
              )}
            </div>
          )}
        </div>
      </main>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Contact</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete {contactToDelete?.name}? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-delete-button">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteContact}
              data-testid="confirm-delete-button"
              className="bg-red-600 hover:bg-red-700"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Add Interaction Modal */}
      <AddInteractionModal
        open={showInteractionModal}
        onOpenChange={setShowInteractionModal}
        onSubmit={handleAddInteraction}
      />
    </div>
  );
};

export default Dashboard;
